import React, { useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import { DatePicker, DatePickerValue } from '../../components/date-picker';
import { formatDate } from '../../utilities';

type RelativesAnswer = 'informed' | 'notInformed';

// Skema 1 and Skema 2 register the orientering of the relatives the same way.
interface RelativesInformation {
  relativesInformedOn?: Date | string;
  relativesNotInformed: boolean;
}

interface UseOfForceRelativesDialogProps {
  current: RelativesInformation;
  onSave: (informedOn: string | undefined, notInformed: boolean) => Promise<void>;
  onClose: () => void;
}

export function UseOfForceRelativesDialog(props: UseOfForceRelativesDialogProps) {
  const { current, onSave, onClose } = props;
  const [answer, setAnswer] = useState<RelativesAnswer | undefined>(initialAnswer(current));
  const [informedOn, setInformedOn] = useState<DatePickerValue>(current.relativesInformedOn ? new Date(current.relativesInformedOn) : undefined);
  const [isSaving, setIsSaving] = useState(false);

  const canSave = answer === 'notInformed' || (answer === 'informed' && informedOn instanceof Date);

  async function save() {
    setIsSaving(true);
    try {
      // Sent as a plain date so the day does not shift with the time zone.
      await onSave(answer === 'informed' ? formatDate(informedOn, 'yyyy-MM-dd') : undefined, answer === 'notInformed');
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Dialog open onClose={() => (isSaving ? undefined : onClose())} fullWidth maxWidth="sm">
      <DialogTitle>Orientering af pårørende eller anden repræsentant</DialogTitle>
      <DialogContent>
        <RadioGroup value={answer ?? ''} onChange={event => setAnswer(event.target.value as RelativesAnswer)}>
          <FormControlLabel value="informed" control={<Radio />} label="Pårørende eller anden repræsentant er orienteret" />
          {answer === 'informed' && <DatePicker label="Dato" value={informedOn} onChange={setInformedOn} maxDate={new Date()} variant="outlined" required />}
          <FormControlLabel
            value="notInformed"
            control={<Radio />}
            label={
              'Fravigelse af orienteringspligten: Pårørende orienteres ikke, da personen frabeder sig dette og ' +
              'selv er i stand til at klage, jf. servicelovens § 135 a, stk. 3.'
            }
          />
        </RadioGroup>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={isSaving}>
          Afbryd
        </Button>
        <Button variant="contained" onClick={save} disabled={isSaving || !canSave}>
          Gem
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function initialAnswer(current: RelativesInformation): RelativesAnswer | undefined {
  if (current.relativesNotInformed) return 'notInformed';
  return current.relativesInformedOn ? 'informed' : undefined;
}
